/**
 * 服务管理服务层
 * 负责所有与后端 API 的交互和业务逻辑处理
 */

import { apiClient } from "../../../config/netConfig";
import { ServiceRecord } from "./serviceTypes";

// API 参数接口
export interface PaginationParams {
  current?: number;
  pageSize?: number;
  name?: string;
  status?: string;
}

export interface CreateServiceParams {
  name: string;
  displayName?: string;
  backendUrl?: string;
  url?: string;
  description?: string;
  healthCheckUrl?: string;
  icon?: string;
  status?: string;
  /** 数字状态：1=运行中, 2=已停止, 3=维护中（表单字段 service_status） */
  service_status?: number;
  routePath?: string;
  menuOrder?: number;
  isMenuItem?: boolean;
  requiresAuth?: boolean;
  required_permissions?: string[];
  isExternal?: boolean;
  serviceCategory?: string;
  serviceType?: number;
  allowed_audience?: string[];
  service_secret?: string;
}

export interface UpdateServiceParams {
  name?: string;
  displayName?: string;
  backendUrl?: string;
  url?: string;
  description?: string;
  healthCheckUrl?: string;
  icon?: string;
  routePath?: string;
  menuOrder?: number;
  isMenuItem?: boolean;
  requiresAuth?: boolean;
  isExternal?: boolean;
  status?: string;
  service_status?: number;
  serviceType?: number;
  allowed_audience?: string[];
  service_secret?: string;
}

// API 服务类
export class ServiceApiService {
  private static normalizeParams(params: any = {}) {
    const pick = (...keys: string[]) => {
      for (const key of keys) {
        if (params[key] !== undefined) {
          return params[key];
        }
      }
      return undefined;
    };

    return {
      name: pick("name", "service_name"),
      displayName: pick("displayName", "display_name"),
      description: pick("description", "service_description"),
      backendUrl: pick("backendUrl", "service_url", "backend_url"),
      url: pick("url", "service_endpoint", "frontend_url"),
      healthCheckUrl: pick("healthCheckUrl", "health_check_url"),
      icon: pick("icon", "icon_name"),
      routePath: pick("routePath", "route_path"),
      menuOrder: pick("menuOrder", "menu_order"),
      isMenuItem: pick("isMenuItem", "is_menu_item"),
      requiresAuth: pick("requiresAuth", "requires_auth"),
      isExternal: pick("isExternal", "is_external"),
      serviceCategory: pick("serviceCategory", "service_category"),
      permissions: pick("permissions", "required_permissions"),
      serviceStatus: pick("service_status", "status"),
      serviceType: pick("serviceType", "service_type"),
      allowed_audience: pick("allowed_audience"),
    };
  }
  /**
   * 获取服务列表
   */
  static async getServiceList(params: PaginationParams = {}) {
    const response = await apiClient.get("/microservices", { params });
    return response.data;
  }

  /**
   * 创建服务
   */
  static async createService(params: CreateServiceParams) {
    const normalized = this.normalizeParams(params);

    const name = normalized.name || params.name;
    const displayName =
      normalized.displayName || params.displayName || params.name;

    // 构建后端API期望的字段格式（混合格式：部分驼峰，部分下划线）
    // 后端期望：backendUrl, url, healthCheckUrl 使用驼峰格式
    // 其他字段使用下划线格式
    const backendParams: Record<string, any> = {
      service_name: name,
      display_name: displayName,
      service_description: normalized.description ?? params.description ?? "",
      // 使用驼峰格式：后端期望 backendUrl, url, healthCheckUrl
      backendUrl: normalized.backendUrl ?? params.backendUrl ?? "", // 后端API URL
      url: normalized.url ?? params.url ?? "", // 前端访问URL
      healthCheckUrl: normalized.healthCheckUrl ?? params.healthCheckUrl ?? null,
      icon_name: normalized.icon ?? params.icon ?? "api",
      route_path: normalized.routePath ?? params.routePath ?? `/${name}`,
      menu_order: normalized.menuOrder ?? params.menuOrder ?? 0,
      // 使用驼峰格式：后端期望 isMenuItem, requiresAuth, isExternal, serviceCategory
      isMenuItem:
        normalized.isMenuItem !== undefined
          ? normalized.isMenuItem
          : params.isMenuItem !== false,
      requiresAuth:
        normalized.requiresAuth !== undefined
          ? normalized.requiresAuth
          : params.requiresAuth !== false,
      required_permissions: (() => {
        const permissionsValue =
          normalized.permissions ?? (params as any).required_permissions ?? `${name}:all:view`;
        // 如果是字符串，转换为数组
        if (typeof permissionsValue === "string") {
          return permissionsValue.trim() ? permissionsValue.split(",").map((p) => p.trim()) : [];
        }
        // 如果已经是数组，直接使用
        if (Array.isArray(permissionsValue)) {
          return permissionsValue;
        }
        // 默认值
        return [`${name}:all:view`];
      })(),
      isExternal:
        normalized.isExternal !== undefined
          ? normalized.isExternal
          : params.isExternal || false,
      serviceCategory:
        normalized.serviceCategory ?? params.serviceCategory ?? "microservice",
      allowed_audience:
        Array.isArray((params as any).allowed_audience)
          ? (params as any).allowed_audience
          : Array.isArray(normalized.allowed_audience)
            ? normalized.allowed_audience
            : [],
      service_secret: params.service_secret || null,
    };

    // 服务类型（0=系统，1=后台，2=中台，3=前台）：优先用表单字段 service_type，确保创建后归到正确分组
    const rawServiceType =
      (params as any).service_type !== undefined && (params as any).service_type !== null
        ? (params as any).service_type
        : normalized.serviceType !== undefined
        ? normalized.serviceType
        : (params as any).serviceType;
    const serviceTypeNum =
      rawServiceType !== undefined && rawServiceType !== null
        ? (typeof rawServiceType === "number" ? rawServiceType : parseInt(String(rawServiceType), 10))
        : 1;
    const finalServiceType = !Number.isNaN(serviceTypeNum) && serviceTypeNum >= 0 && serviceTypeNum <= 3 ? serviceTypeNum : 1;
    (backendParams as any).service_type = finalServiceType;
    (backendParams as any).serviceType = finalServiceType;

    // 服务状态（1=运行中，2=已停止，3=维护中）：优先用表单字段 service_status，确保创建时按选择保存
    const rawServiceStatus =
      (params as any).service_status !== undefined && (params as any).service_status !== null
        ? (params as any).service_status
        : normalized.serviceStatus !== undefined
        ? normalized.serviceStatus
        : (params as any).status;
    const statusNum =
      rawServiceStatus !== undefined && rawServiceStatus !== null
        ? (typeof rawServiceStatus === "number" ? rawServiceStatus : parseInt(String(rawServiceStatus), 10))
        : 1;
    const finalStatus = !Number.isNaN(statusNum) && statusNum >= 1 && statusNum <= 3 ? statusNum : 1;
    (backendParams as any).service_status = finalStatus;

    const response = await apiClient.post("/microservices", backendParams);
    return response.data;
  }

  /**
   * 更新服务
   * 注意：后端API期望接收驼峰格式（camelCase）的字段名，然后内部映射到下划线格式
   */
  static async updateService(id: string, params: UpdateServiceParams) {
    const normalized = this.normalizeParams(params);

    // 构建请求体，使用驼峰格式（后端会将其映射到下划线格式）
    const payload: Record<string, any> = {};

    // 处理服务名称字段
    if (normalized.name !== undefined) {
      payload.name = normalized.name === '' ? null : normalized.name;
    } else if (params.name !== undefined) {
      payload.name = params.name === '' ? null : params.name;
    }

    // 处理显示名称字段
    if (normalized.displayName !== undefined) {
      payload.displayName = normalized.displayName === '' ? null : normalized.displayName;
    } else if (params.displayName !== undefined) {
      payload.displayName = params.displayName === '' ? null : params.displayName;
    }

    // 处理服务描述字段
    if (normalized.description !== undefined) {
      payload.description = normalized.description === '' ? null : normalized.description;
    } else if (params.description !== undefined) {
      payload.description = params.description === '' ? null : params.description;
    }

    // 处理后端URL字段（后端API URL）- 后端期望 backendUrl
    if (normalized.backendUrl !== undefined) {
      payload.backendUrl = normalized.backendUrl === '' ? null : normalized.backendUrl;
    } else if (params.backendUrl !== undefined) {
      payload.backendUrl = params.backendUrl === '' ? null : params.backendUrl;
    }

    // 处理前端URL字段（前端访问URL）- 后端期望 url
    if (normalized.url !== undefined) {
      payload.url = normalized.url === '' ? null : normalized.url;
    } else if (params.url !== undefined) {
      payload.url = params.url === '' ? null : params.url;
    }

    // 处理健康检查URL字段
    if (normalized.healthCheckUrl !== undefined) {
      payload.healthCheckUrl = normalized.healthCheckUrl === '' ? null : normalized.healthCheckUrl;
    } else if (params.healthCheckUrl !== undefined) {
      payload.healthCheckUrl = params.healthCheckUrl === '' ? null : params.healthCheckUrl;
    }

    // 处理图标字段 - 后端期望 icon_name（下划线格式）
    if (normalized.icon !== undefined) {
      payload.icon_name = normalized.icon === '' ? null : normalized.icon;
    } else if (params.icon !== undefined) {
      payload.icon_name = params.icon === '' ? null : params.icon;
    }

    // 处理路由路径字段
    if (normalized.routePath !== undefined) {
      payload.routePath = normalized.routePath === '' ? null : normalized.routePath;
    } else if (params.routePath !== undefined) {
      payload.routePath = params.routePath === '' ? null : params.routePath;
    }

    // 处理菜单顺序字段
    if (normalized.menuOrder !== undefined) {
      payload.menuOrder = normalized.menuOrder;
    } else if (params.menuOrder !== undefined) {
      payload.menuOrder = params.menuOrder;
    }

    // 处理是否菜单项字段
    if (normalized.isMenuItem !== undefined) {
      payload.isMenuItem = normalized.isMenuItem;
    } else if (params.isMenuItem !== undefined) {
      payload.isMenuItem = params.isMenuItem;
    }

    // 处理是否需要认证字段
    if (normalized.requiresAuth !== undefined) {
      payload.requiresAuth = normalized.requiresAuth;
    } else if (params.requiresAuth !== undefined) {
      payload.requiresAuth = params.requiresAuth;
    }

    // 处理是否外部服务字段
    if (normalized.isExternal !== undefined) {
      payload.external = normalized.isExternal;
    } else if (params.isExternal !== undefined) {
      payload.external = params.isExternal;
    }

    // 处理权限字段：将字符串转换为数组
    if (normalized.permissions !== undefined) {
      if (typeof normalized.permissions === "string") {
        // 字符串格式：转换为数组
        payload.permissions = normalized.permissions.trim()
          ? normalized.permissions.split(",").map((p: string) => p.trim())
          : [];
      } else if (Array.isArray(normalized.permissions)) {
        // 已经是数组，直接使用
        payload.permissions = normalized.permissions;
      } else {
        payload.permissions = [];
      }
    } else if ((params as any).required_permissions !== undefined) {
      // 兼容下划线格式的权限字段
      if (typeof (params as any).required_permissions === "string") {
        // 字符串格式：转换为数组
        payload.permissions = (params as any).required_permissions.trim()
          ? (params as any).required_permissions.split(",").map((p: string) => p.trim())
          : [];
      } else if (Array.isArray((params as any).required_permissions)) {
        // 已经是数组，直接使用
        payload.permissions = (params as any).required_permissions;
      } else {
        payload.permissions = [];
      }
    }

    // 处理服务状态字段（数字类型：1=运行中，2=已停止，3=维护中）
    if (normalized.serviceStatus !== undefined || params.service_status !== undefined) {
      const serviceStatusValue = normalized.serviceStatus !== undefined
        ? normalized.serviceStatus
        : params.service_status;
      if (serviceStatusValue !== undefined && serviceStatusValue !== null) {
        // 如果是数字类型，直接发送
        if (typeof serviceStatusValue === "number") {
          payload.service_status = serviceStatusValue;
        } else if (typeof serviceStatusValue === "string") {
          // 如果是字符串，尝试转换为数字
          const numValue = parseInt(String(serviceStatusValue), 10);
          if (!isNaN(numValue) && numValue >= 1 && numValue <= 3) {
            payload.service_status = numValue;
          }
        }
      }
    }

    // 处理服务类型字段（数字类型：0=系统，1=后台，2=中台，3=前台）
    if (normalized.serviceType !== undefined || params.serviceType !== undefined || (params as any).service_type !== undefined) {
      const serviceTypeValue = normalized.serviceType !== undefined
        ? normalized.serviceType
        : params.serviceType !== undefined
          ? params.serviceType
          : (params as any).service_type;
      if (serviceTypeValue !== undefined && serviceTypeValue !== null) {
        // 如果是数字类型，直接发送
        if (typeof serviceTypeValue === "number") {
          payload.service_type = serviceTypeValue;
        } else if (typeof serviceTypeValue === "string") {
          // 如果是字符串，尝试转换为数字
          const numValue = parseInt(String(serviceTypeValue), 10);
          if (!isNaN(numValue) && numValue >= 0 && numValue <= 3) {
            payload.service_type = numValue;
          }
        }
      }
    }

    // 处理允许的受众字段 - 后端期望 allowed_audience（下划线格式）
    // 确保即使字段存在但值为空数组时也发送
    const allowedAudienceValue =
      (params as any).allowed_audience !== undefined
        ? (params as any).allowed_audience
        : normalized.allowed_audience !== undefined
          ? normalized.allowed_audience
          : undefined;

    // 如果字段存在（包括空数组），则添加到 payload（使用下划线格式）
    if (allowedAudienceValue !== undefined) {
      payload.allowed_audience = Array.isArray(allowedAudienceValue)
        ? allowedAudienceValue
        : [];
      console.log("[更新服务] allowed_audience 值:", payload.allowed_audience);
    }

    // 处理服务秘钥字段
    if (params.service_secret !== undefined) {
      payload.service_secret = params.service_secret === '' ? null : params.service_secret;
    }

    // 调试：输出发送的 payload（详细内容）
    console.log("📤 发送更新服务的请求:", {
      id,
      payload: JSON.parse(JSON.stringify(payload)), // 深拷贝以便查看完整内容
      payloadKeys: Object.keys(payload),
    });
    console.log("📤 Payload 详细内容:", JSON.stringify(payload, null, 2));

    try {
      const response = await apiClient.put(`/microservices/${id}`, payload);
      return response.data;
    } catch (error: any) {
      console.error("❌ 更新服务失败:", {
        error,
        id,
        payload,
        response: error.response?.data,
        status: error.response?.status,
        errorMessage: error.response?.data?.message || error.message,
        errorDetails: error.response?.data?.error || error.response?.data?.details,
      });
      // 输出后端返回的完整错误信息
      if (error.response?.data) {
        console.error("❌ 后端错误详情:", JSON.stringify(error.response.data, null, 2));
      }
      throw error;
    }
  }

  /**
   * 删除服务
   */
  static async deleteService(id: string) {
    console.log('[ServiceApiService.deleteService] 删除服务请求:', { id, idType: typeof id });

    if (!id) {
      console.error('[ServiceApiService.deleteService] 服务ID为空');
      throw new Error('服务ID不能为空');
    }

    try {
      const response = await apiClient.delete(`/microservices/${id}`);
      console.log('[ServiceApiService.deleteService] 删除成功:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[ServiceApiService.deleteService] 删除失败:', {
        id,
        error: error.response?.data || error.message,
        status: error.response?.status
      });
      throw error;
    }
  }

  /**
   * 健康检查
   */
  static async healthCheck(id: string) {
    const response = await apiClient.post(`/microservices/${id}/health-check`);
    return response.data;
  }

  /**
   * 获取服务详情
   */
  static async getServiceDetail(id: string) {
    const response = await apiClient.get(`/microservices/${id}`);
    return response.data;
  }

  /** 数字 service_status(1/2/3) 转为前端使用的 status 字符串 */
  private static normalizeStatus(service: any): string {
    const raw = service.status ?? service.service_status;
    if (typeof raw === "string" && ["online", "offline", "maintenance"].includes(raw)) {
      return raw;
    }
    const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
    if (n === 1) return "online";
    if (n === 2) return "offline";
    if (n === 3) return "maintenance";
    return "online";
  }

  /**
   * 数据转换：将 API 响应转换为 ServiceRecord 格式
   */
  static transformServiceData(service: any): ServiceRecord {
    // 统一解析服务类型（0=系统，1=后台，2=中台，3=前台）
    const rawServiceType = service.service_type ?? service.serviceType;
    let parsedServiceType: number | undefined;
    if (typeof rawServiceType === "number") {
      parsedServiceType = rawServiceType;
    } else if (
      rawServiceType !== undefined &&
      rawServiceType !== null &&
      rawServiceType !== ""
    ) {
      const n = parseInt(String(rawServiceType), 10);
      if (!Number.isNaN(n)) {
        parsedServiceType = n;
      }
    }

    return {
      id: service.id || `service_${Date.now()}_${Math.random()}`,
      key: service.key || service.id || `service_${Date.now()}_${Math.random()}`,
      name: service.name || service.key || service.serviceName || service.service_name || "未知服务",
      displayName: service.displayName || service.display_name || service.name || service.serviceName || service.service_name || "未知服务",
      description: service.description || service.serviceDescription || service.service_description || "",
      status: this.normalizeStatus(service),
      healthCheckUrl: service.healthCheckUrl || service.health_check_url || "",
      lastHealthCheck: service.lastHealthCheck || service.last_health_check || "",
      version: service.version || "",
      icon: service.icon || service.iconName || service.icon_name || "",
      color: service.color || "",
      isSystemService: service.isSystemService || service.is_system_service || false,
      routePath: service.routePath || service.route_path || "",
      menuOrder: service.menuOrder || service.menu_order || 0,
      isMenuItem: service.isMenuItem || service.is_menu_item || false,
      backendUrl: service.backendUrl || service.serviceEndpoint || service.service_endpoint || "",
      url: service.url || service.serviceUrl || service.service_url || "",
      requiresAuth: service.requiresAuth || service.requires_auth || false,
      permissions: service.permissions || service.requiredPermissions || service.required_permissions || [],
      external: service.external || service.isExternal || service.is_external || false,
      has_token: service.hasToken || service.has_token || false,
      token_info: service.tokenInfo || service.token_info || null,
      healthStatus: service.healthStatus || service.health_status || false,
      registered_operations: service.registered_operations || 0,
      actual_permissions_count: undefined, // 将在业务层计算
      // allowed_audience 现在从 service_token 表中获取，后端已经在返回数据中包含此字段
      allowed_audience: Array.isArray(service.allowed_audience)
        ? service.allowed_audience.filter((item: any) => item !== null && item !== undefined && item !== '')
        : [],
      // 服务秘钥字段
      service_secret: service.service_secret || null,
      // 服务类型字段
      serviceType: parsedServiceType,
      service_type: parsedServiceType,
    };
  }

  /**
   * 处理服务列表响应
   */
  static processServiceListResponse(response: any): { services: ServiceRecord[]; total: number } {
    // 调试信息已移除

    if (!response.success) {
      return { services: [], total: 0 };
    }

    const servicesArray = Array.isArray(response.data) ? response.data : response.data?.services || [];
    // 调试信息已移除

    const transformedServices = servicesArray.map((service: any) => this.transformServiceData(service));

    // 去重处理
    const uniqueServices = transformedServices.reduce((acc: ServiceRecord[], service: ServiceRecord) => {
      const serviceWithId = {
        ...service,
        id: service.id || `service_${service.name}_${Date.now()}_${Math.random()}`,
      };
      if (!acc.find((s) => s.id === serviceWithId.id)) {
        acc.push(serviceWithId);
      }
      return acc;
    }, []);

    const total = response.data?.pagination?.total || response.data?.total || response.pagination?.total || servicesArray.length;

    return { services: uniqueServices, total };
  }

}
